import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepContent from '@material-ui/core/StepContent';
import Typography from '@material-ui/core/Typography';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
  AiOutlineCheckSquare,
  AiOutlineFileDone,
  BsCardChecklist,
  BsGrid,
  BiDna,
  FiShoppingCart,
  GiCheckboxTree,
  MdRadioButtonChecked, MdNavigation,
} from 'react-icons/all';
import { useThematicPackageRoadmap } from '../../../hooks/thematic-package-roadmap';
import AppLoader from '../../basic/AppLoader';
import AppButtonMinimal from '../../basic/AppButtonMinimal';
import { getTheme } from '../../../theme';
import { ThematicPackageStatus } from '../../../cassandra-shared-js/thematic-package-status-enum';
import { useAlerts } from '../../../hooks/alerts-hooks';
import RolesComponent from '../../Wrappers/RolesComponent';

const useStyles = makeStyles((theme) => {
  return {
    root: {
      width: '100%',
    },
    button: {
      marginTop: theme.spacing(1),
      marginRight: theme.spacing(1),
    },
    actionsContainer: {
      marginBottom: theme.spacing(2),
    },
  };
});

const resolveIcon = (status) => {
  switch (status) {
    case ThematicPackageStatus.FIRST_COMMERCIAL_CHECK:
      return AiOutlineCheckSquare;
    case ThematicPackageStatus.FIRST_SCIENTIFIC_CHECK:
      return BiDna;
    case ThematicPackageStatus.ANALYSIS_CHECK:
      return BsCardChecklist;
    case ThematicPackageStatus.DIAGNOSTIC_PACKAGE_CHECK:
      return GiCheckboxTree;
    case ThematicPackageStatus.REPORT_CHECK:
      return BsGrid;
    case ThematicPackageStatus.QUALITY_CHECK:
      return MdRadioButtonChecked;
    case ThematicPackageStatus.SECOND_COMMERCIAL_CHECK:
      return MdNavigation;
    case ThematicPackageStatus.DEPLOY_CHECK:
      return FiShoppingCart;
    case ThematicPackageStatus.PRODUCT:
      return AiOutlineFileDone;
    default:
      return AiOutlineCheckSquare;
  }
};

export default function ThematicPackageRoadmap(
  {
    id,
  },
) {
  const classes = useStyles();

  const {
    currentStatus,
    statuses,
    roadmapLoading,
    acceptThematicPackageStatus,
    rejectThematicPackageStatus,
  } = useThematicPackageRoadmap(id);

  const [ isProceeding, setIsProceeding ] = useState(false);

  const { alertError } = useAlerts();

  const handleProceed = (fn) => {
    return Promise.resolve()
      .then(() => { setIsProceeding(true); })
      .then(() => { return fn(); })
      .catch((e) => { return alertError(JSON.stringify(e)); })
      .finally(() => { setIsProceeding(false); });
  };

  const handleAccept = () => {
    return handleProceed(acceptThematicPackageStatus);
  };

  const handleReject = () => {
    return handleProceed(rejectThematicPackageStatus);
  };

  const disabledCTA = isProceeding;

  const activeStepIdx = statuses.findIndex((s) => { return s.tag === currentStatus; });

  if (roadmapLoading) {
    return <AppLoader />;
  }

  return (
    <div className={ classes.root }>
      {disabledCTA && <LinearProgress />}
      <Stepper activeStep={ activeStepIdx || 0 } orientation='vertical'>
        {statuses.map(({ name, description, tag, proceedPermissionRoles }, idx) => {
          return (
            <Step key={ tag }>
              <StepLabel
                StepIconComponent={ ({ completed, active }) => {
                  const color = completed
                    ? getTheme().palette.success.main
                    : active
                      ? getTheme().palette.warning.main
                      : getTheme().palette.text.disabled;
                  const size = active ? '36px' : '24px';
                  const Icon = resolveIcon(tag);
                  return <Icon size={ size } color={ color } />;
                } }
              >
                {name}
              </StepLabel>
              { activeStepIdx !== statuses.length - 1
              && (
                <StepContent>
                  <Typography>{description}</Typography>
                  <RolesComponent roles={ proceedPermissionRoles }>
                    <div className={ classes.actionsContainer }>
                      <div>
                        <AppButtonMinimal
                          disabled={ 0 === idx || disabledCTA }
                          onClick={ () => { return handleReject(); } }
                          className={ classes.button }
                          color={ getTheme().palette.error.main }
                          text='Reject'
                        />
                        <AppButtonMinimal
                          disabled={ disabledCTA }
                          onClick={ () => { return handleAccept(); } }
                          className={ classes.button }
                          color={ getTheme().palette.success.main }
                          text={ idx === statuses.length - 1 ? 'Complete' : 'Approve' }
                        />
                      </div>
                    </div>
                  </RolesComponent>
                </StepContent>
              )}
            </Step>
          );
        })}
      </Stepper>
    </div>
  );
}

